import {
  describe,
  it,
  expect,
  vi,
  beforeEach,
  afterEach,
  beforeAll,
  afterAll,
} from "vitest";
import { searchProject, SearchResult, SearchOptions } from "./search";
import * as fs from "fs";
import * as path from "path";
import { glob } from "glob";

// Mock console.error，避免测试输出污染
vi.spyOn(console, "error").mockImplementation(() => {});

// Mock 依赖
vi.mock("fs", () => ({
  readFileSync: vi.fn((file) => {
    if (file.includes("testFile.ts"))
      return "const testFile = 1;\n// test message";
    if (file.includes("utils.ts"))
      return "function utilFunc() { return true; }";
    if (file.includes("content.ts")) return "before\ntarget\nafter";
    return "function testFunction() { return true; }\nclass TestClass {}\ntest message";
  }),
  statSync: vi
    .fn()
    .mockReturnValue({ isFile: () => true, isDirectory: () => false }),
}));

vi.mock("path", () => ({
  relative: vi.fn((root, file) => file.replace(root + "/", "")),
  basename: vi.fn((file) => file.split("/").pop()),
  resolve: vi.fn((...args) => args.join("/")),
}));

vi.mock("glob", () => ({
  glob: vi.fn().mockImplementation((pattern, opts) => {
    // 针对符号搜索测试，始终返回 testFile.ts
    return Promise.resolve(["testFile.ts"]);
  }),
}));

vi.mock("./utils/os_utils", () => ({
  detectDominantLanguages: vi.fn().mockReturnValue(["typescript"]),
  getLanguageFileExtensions: vi.fn().mockReturnValue("ts"),
}));

vi.mock("./utils/lsp_core", () => {
  class MockLSPClient {
    constructor() {}
    async start() {}
    async shutdown() {}
    async sendDidOpen() {}
    async sendDidClose() {}
    async sendDocumentSymbol() {
      return [
        {
          kind: 12, // Function
          name: "testFunction",
          location: {
            range: {
              start: { line: 0, character: 0 },
              end: { line: 1, character: 0 },
            },
          },
        },
        {
          kind: 5, // Class
          name: "TestClass",
          location: {
            range: {
              start: { line: 2, character: 0 },
              end: { line: 3, character: 0 },
            },
          },
        },
      ];
    }
  }
  return {
    LSPClient: MockLSPClient,
    SymbolKind: {
      File: 1,
      Module: 2,
      Namespace: 3,
      Package: 4,
      Class: 5,
      Method: 6,
      Property: 7,
      Field: 8,
      Constructor: 9,
      Enum: 10,
      Interface: 11,
      Function: 12,
      Variable: 13,
      Constant: 14,
      String: 15,
      Number: 16,
      Boolean: 17,
      Array: 18,
      Object: 19,
      Key: 20,
      Null: 21,
      EnumMember: 22,
      Struct: 23,
      Event: 24,
      Operator: 25,
      TypeParameter: 26,
    },
    DEFAULT_CONFIG: {},
  };
});

vi.mock("./semantic_search", () => ({
  semanticSearch: vi.fn(),
}));

describe("search", () => {
  beforeEach(() => {
    vi.clearAllMocks();
  });

  afterEach(() => {
    vi.restoreAllMocks();
  });

  describe("searchProject 主函数", () => {
    it("应该返回空数组当没有找到结果时", async () => {
      const mockGlob = vi.mocked(glob);
      mockGlob.mockResolvedValue([]);

      const result = await searchProject("nonexistent", "/project/root");

      expect(result).toEqual([]);
    });

    it("应该处理语义搜索", async () => {
      const mockSemanticSearch = vi.mocked(
        await import("./semantic_search"),
      ).semanticSearch;
      mockSemanticSearch.mockResolvedValue([
        {
          type: "semantic",
          name: "testFunction",
          file: "src/test.ts",
          line: 1,
          column: 1,
          description: "Test function",
          kind: 12,
          relevance: 0.9,
          context: "function testFunction() {}",
          keywords: ["test"],
          parameters: [],
          returnType: "void",
          callers: [],
          callees: [],
          complexity: 1,
          callCount: 0,
        },
      ]);

      const options: SearchOptions = { semantic: true };
      const result = await searchProject("test", "/project/root", options);

      expect(result.length).toBeGreaterThan(0);
      expect(result[0].type).toBe("semantic");
      expect(result[0].relevance).toBe(0.9);
    });

    it("应该应用限制参数", async () => {
      const mockGlob = vi.mocked(glob);
      const mockFs = vi.mocked(fs);
      const mockPath = vi.mocked(path);

      mockGlob.mockResolvedValue([
        "/project/root/src/test1.ts",
        "/project/root/src/test2.ts",
      ]);
      mockFs.statSync.mockReturnValue({ isFile: () => true } as any);
      mockPath.relative.mockReturnValue("src/test.ts");
      mockPath.basename.mockReturnValue("test.ts");

      const options: SearchOptions = { limit: 1 };
      const result = await searchProject("test", "/project/root", options);

      expect(result.length).toBeLessThanOrEqual(1);
    });

    it("应该按相关性排序结果", async () => {
      const mockSemanticSearch = vi.mocked(
        await import("./semantic_search"),
      ).semanticSearch;
      mockSemanticSearch.mockResolvedValue([
        {
          type: "semantic",
          name: "lowRelevance",
          file: "src/low.ts",
          line: 1,
          column: 1,
          description: "Low relevance",
          kind: 12,
          relevance: 0.3,
          context: "function lowRelevance() {}",
          keywords: ["low"],
          parameters: [],
          returnType: "void",
          callers: [],
          callees: [],
          complexity: 1,
          callCount: 0,
        },
        {
          type: "semantic",
          name: "highRelevance",
          file: "src/high.ts",
          line: 1,
          column: 1,
          description: "High relevance",
          kind: 12,
          relevance: 0.9,
          context: "function highRelevance() {}",
          keywords: ["high"],
          parameters: [],
          returnType: "void",
          callers: [],
          callees: [],
          complexity: 1,
          callCount: 0,
        },
      ]);

      const options: SearchOptions = { semantic: true };
      const result = await searchProject("test", "/project/root", options);

      expect(result.length).toBeGreaterThan(1);
      const rel0 = typeof result[0].relevance === 'number' ? result[0].relevance : 0;
      const rel1 = typeof result[1].relevance === 'number' ? result[1].relevance : 0;
      expect(rel0).toBeGreaterThan(rel1);
    });
  });

  describe("文件搜索测试", () => {
    it("应该搜索文件名", async () => {
      const mockGlob = vi.mocked(glob);
      const mockFs = vi.mocked(fs);
      const mockPath = vi.mocked(path);

      mockGlob.mockResolvedValue(["/project/root/src/testFile.ts"]);
      mockFs.statSync.mockReturnValue({ isFile: () => true } as any);
      mockPath.relative.mockReturnValue("src/testFile.ts");
      mockPath.basename.mockReturnValue("testFile.ts");

      const options: SearchOptions = { type: "file" };
      const result = await searchProject("testFile", "/project/root", options);

      expect(result.length).toBeGreaterThan(0);
      expect(result[0].type).toBe("file");
      expect(result[0].name).toBe("testFile.ts");
    });

    it("应该搜索文件路径", async () => {
      const mockGlob = vi.mocked(glob);
      const mockFs = vi.mocked(fs);
      const mockPath = vi.mocked(path);

      mockGlob.mockResolvedValue(["/project/root/src/utils/helper.ts"]);
      mockFs.statSync.mockReturnValue({ isFile: () => true } as any);
      mockPath.relative.mockReturnValue("src/utils/helper.ts");
      mockPath.basename.mockReturnValue("helper.ts");

      const options: SearchOptions = { type: "file" };
      const result = await searchProject("utils", "/project/root", options);

      expect(result.length).toBeGreaterThan(0);
      expect(result[0].file).toContain("utils");
    });

    it("应该忽略目录", async () => {
      const mockGlob = vi.mocked(glob);
      const mockFs = vi.mocked(fs);
      const mockPath = vi.mocked(path);

      mockGlob.mockResolvedValue(["/project/root/src/testDir"]);
      mockFs.statSync.mockReturnValue({ isFile: () => false } as any);
      mockPath.relative.mockReturnValue("src/testDir");
      mockPath.basename.mockReturnValue("testDir");

      const options: SearchOptions = { type: "file" };
      const result = await searchProject("testDir", "/project/root", options);

      expect(result.length).toBe(0);
    });
  });

  describe("符号搜索测试", () => {
    it("应该搜索函数", async () => {
      const mockGlob = vi.mocked(glob);
      const mockFs = vi.mocked(fs);
      const mockPath = vi.mocked(path);
      const mockOsUtils = vi.mocked(await import("./utils/os_utils"));

      mockGlob.mockResolvedValue(["/project/root/src/test.ts"]);
      mockFs.readFileSync.mockReturnValue("function testFunction() {}");
      mockPath.relative.mockReturnValue("src/test.ts");
      mockOsUtils.detectDominantLanguages.mockReturnValue(["typescript"]);
      mockOsUtils.getLanguageFileExtensions.mockReturnValue("ts");

      const options: SearchOptions = { type: "function" };
      const result = await searchProject(
        "testFunction",
        "/project/root",
        options,
      );

      expect(result.length).toBeGreaterThan(0);
      expect(result[0].type).toBe("function");
    });

    it("应该搜索类", async () => {
      const mockGlob = vi.mocked(glob);
      const mockFs = vi.mocked(fs);
      const mockPath = vi.mocked(path);
      const mockOsUtils = vi.mocked(await import("./utils/os_utils"));

      mockGlob.mockResolvedValue(["/project/root/src/test.ts"]);
      mockFs.readFileSync.mockReturnValue("class TestClass {}");
      mockPath.relative.mockReturnValue("src/test.ts");
      mockOsUtils.detectDominantLanguages.mockReturnValue(["typescript"]);
      mockOsUtils.getLanguageFileExtensions.mockReturnValue("ts");

      const options: SearchOptions = { type: "class" };
      const result = await searchProject("TestClass", "/project/root", options);

      expect(result.length).toBeGreaterThan(0);
      expect(result[0].type).toBe("class");
    });

    it("应该处理 LSP 错误", async () => {
      const mockGlob = vi.mocked(glob);
      const mockFs = vi.mocked(fs);
      const mockPath = vi.mocked(path);
      const mockOsUtils = vi.mocked(await import("./utils/os_utils"));

      mockGlob.mockResolvedValue(["/project/root/src/test.ts"]);
      mockFs.readFileSync.mockReturnValue("function test() {}");
      mockPath.relative.mockReturnValue("src/test.ts");
      mockOsUtils.detectDominantLanguages.mockReturnValue(["typescript"]);
      mockOsUtils.getLanguageFileExtensions.mockReturnValue("ts");

      const options: SearchOptions = { type: "function" };
      const result = await searchProject("test", "/project/root", options);

      expect(Array.isArray(result)).toBe(true);
    });
  });

  describe("内容搜索测试", () => {
    it("应该搜索文件内容", async () => {
      const mockGlob = vi.mocked(glob);
      const mockFs = vi.mocked(fs);
      const mockPath = vi.mocked(path);

      const fileContent = `
        function testFunction() {
          console.log("test message");
          return true;
        }
      `;

      mockGlob.mockResolvedValue(["/project/root/src/test.ts"]);
      mockFs.readFileSync.mockReturnValue(fileContent);
      mockPath.relative.mockReturnValue("src/test.ts");

      const options: SearchOptions = { type: "content" };
      const result = await searchProject(
        "test message",
        "/project/root",
        options,
      );

      expect(result.length).toBeGreaterThan(0);
      expect(result[0].type).toBe("content");
      expect(result[0].content).toContain("test message");
    });

    it("应该处理大小写敏感搜索", async () => {
      const mockGlob = vi.mocked(glob);
      const mockFs = vi.mocked(fs);
      const mockPath = vi.mocked(path);

      const fileContent = "function TestFunction() { return true; }";

      mockGlob.mockResolvedValue(["/project/root/src/test.ts"]);
      mockFs.readFileSync.mockReturnValue(fileContent);
      mockPath.relative.mockReturnValue("src/test.ts");

      const options: SearchOptions = { type: "content", caseSensitive: true };
      const result = await searchProject(
        "testfunction",
        "/project/root",
        options,
      );

      expect(result.length).toBe(0);
    });

    it("应该处理正则表达式搜索", async () => {
      const mockGlob = vi.mocked(glob);
      const mockFs = vi.mocked(fs);
      const mockPath = vi.mocked(path);

      const fileContent = "function test123() { return true; }";

      mockGlob.mockResolvedValue(["/project/root/src/test.ts"]);
      mockFs.readFileSync.mockReturnValue(fileContent);
      mockPath.relative.mockReturnValue("src/test.ts");

      const options: SearchOptions = { type: "content", regex: true };
      const result = await searchProject("test\\d+", "/project/root", options);

      expect(result.length).toBeGreaterThan(0);
    });

    it("应该包含上下文行", async () => {
      const mockGlob = vi.mocked(glob);
      const mockFs = vi.mocked(fs);
      const mockPath = vi.mocked(path);

      const fileContent = `
        function before() {}
        function target() {
          console.log("target");
        }
        function after() {}
      `;

      mockGlob.mockResolvedValue(["/project/root/src/test.ts"]);
      mockFs.readFileSync.mockReturnValue(fileContent);
      mockPath.relative.mockReturnValue("src/test.ts");

      const options: SearchOptions = { type: "content", contextLines: 1 };
      const result = await searchProject("target", "/project/root", options);

      expect(result.length).toBeGreaterThan(0);
      expect(result[0].context).toContain("before");
      expect(result[0].context).toContain("target");
    });
  });

  describe("搜索类型过滤测试", () => {
    it("应该只搜索指定类型", async () => {
      const mockGlob = vi.mocked(glob);
      const mockFs = vi.mocked(fs);
      const mockPath = vi.mocked(path);
      const mockOsUtils = vi.mocked(await import("./utils/os_utils"));

      mockGlob.mockResolvedValue(["/project/root/src/test.ts"]);
      mockFs.readFileSync.mockReturnValue("function test() {}");
      mockPath.relative.mockReturnValue("src/test.ts");
      mockOsUtils.detectDominantLanguages.mockReturnValue(["typescript"]);
      mockOsUtils.getLanguageFileExtensions.mockReturnValue("ts");

      const options: SearchOptions = { type: "function" };
      const result = await searchProject("test", "/project/root", options);

      // 所有结果都应该是函数类型
      result.forEach((r) => {
        expect(r.type).toBe("function");
      });
    });

    it("应该搜索所有类型", async () => {
      const mockGlob = vi.mocked(glob);
      const mockFs = vi.mocked(fs);
      const mockPath = vi.mocked(path);
      const mockOsUtils = vi.mocked(await import("./utils/os_utils"));

      mockGlob.mockResolvedValue(["/project/root/src/test.ts"]);
      mockFs.readFileSync.mockReturnValue("function test() {}");
      mockPath.relative.mockReturnValue("src/test.ts");
      mockOsUtils.detectDominantLanguages.mockReturnValue(["typescript"]);
      mockOsUtils.getLanguageFileExtensions.mockReturnValue("ts");

      const options: SearchOptions = { type: "all" };
      const result = await searchProject("test", "/project/root", options);

      // 应该包含多种类型的结果
      const types = result.map((r) => r.type);
      expect(types.length).toBeGreaterThan(0);
    });
  });

  describe("错误处理测试", () => {
    beforeAll(() => {
      vi.spyOn(console, "error").mockImplementation(() => {});
    });
    afterAll(() => {
      vi.restoreAllMocks();
    });
    it("应该处理 glob 错误", async () => {
      const options: SearchOptions = { type: "file" };
      // mock glob 抛出错误
      const mockGlob = vi.mocked(glob);
      mockGlob.mockRejectedValueOnce(new Error("Glob error"));
      const result = await searchProject("error", "/project/root", options);
      expect(result).toEqual([]);
    });
    it("应该处理文件读取错误", async () => {
      const options: SearchOptions = { type: "file" };
      // mock fs.readFileSync 抛出错误
      const mockFs = vi.mocked(fs);
      mockFs.readFileSync.mockImplementationOnce(() => {
        throw new Error("Read error");
      });
      const result = await searchProject("error", "/project/root", options);
      expect(result).toEqual([]);
    });
  });
});
